var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import jQuery from 'jquery';
import clsx from 'clsx';
import { useIntersectionObserver } from '@themes/common/components';
import { algoliaConfig, algoliaRecommendClient, algoliaSearchClient } from '../common';
import LoadSwiperJS, { SwiperInjectStyles } from '@themes/common/lazy-swiper';
import EmptyCard from '../EmptyCard';
import { DistinctFrom } from '@commons/helpers';
const prefix = globalThis.algoliaShopify.config.index_prefix;
const postfix = (_a = globalThis.algoliaShopify.indexNamePostfix) !== null && _a !== void 0 ? _a : '';
const indexName = prefix + 'products' + postfix;
const excludeHiddenTags = 'NOT tags:hidden';
const commaSplit = (data) => { var _a; return ((_a = data === null || data === void 0 ? void 0 : data.toString().split(',')) !== null && _a !== void 0 ? _a : []).filter((f) => !!f); };
const generateFilters = (ids, field = 'objectID') => `${excludeHiddenTags} AND (${ids.map((id) => `${field}:${id}`).join(' OR ')})`;
function AlgoliaRecomendationItem({ item }) {
    const element = useRef(null);
    const { algolia } = useMemo(() => algoliaConfig(), []);
    useEffect(() => {
        var _a, _b;
        if (element.current) {
            (_a = algolia === null || algolia === void 0 ? void 0 : algolia.externals) === null || _a === void 0 ? void 0 : _a.render(algolia === null || algolia === void 0 ? void 0 : algolia.instantSearchProductTemplate(item, algolia === null || algolia === void 0 ? void 0 : algolia.externals.html, {
                Highlight: (_b = algolia === null || algolia === void 0 ? void 0 : algolia.externals) === null || _b === void 0 ? void 0 : _b.instantsearch.highlight,
            }), element.current);
        }
    }, [algolia, item]);
    const handleClick = useCallback((e) => {
        var _a, _b, _c;
        const container = document.querySelector('.ais-hits-container');
        const collectionPageEnabled = (algolia === null || algolia === void 0 ? void 0 : algolia.is_collection_results_page) && (algolia === null || algolia === void 0 ? void 0 : algolia.config.instant_search_enabled_on_collection);
        let collectionHandle = null;
        if (collectionPageEnabled) {
            var matches = window.location.pathname.match(/\/collections\/([^/]+)/i);
            collectionHandle = Boolean(matches) && (matches === null || matches === void 0 ? void 0 : matches.length) === 2 ? matches === null || matches === void 0 ? void 0 : matches[1] : null;
        }
        const insightsClient = (_a = algolia === null || algolia === void 0 ? void 0 : algolia.externals) === null || _a === void 0 ? void 0 : _a.insightsClient;
        let el = e.target;
        let hit;
        do {
            if (el.matches('.ais-hit')) {
                hit = el;
                break;
            }
            el = el.parentNode;
        } while (el && el !== container);
        if (!hit) {
            return;
        }
        const $this = hit;
        const productHandle = $this.dataset.handle;
        const variant_id = $this.dataset.variantId;
        const distinct = $this.dataset.distinct;
        // Construct the link we should redirect to
        let link = (_b = globalThis.Shopify.routes.root) !== null && _b !== void 0 ? _b : '/';
        // If we're on a collection page and the handle is available
        // then prefix the product link with the collection handle
        if (collectionPageEnabled && collectionHandle) {
            link += 'collections/' + collectionHandle;
        }
        // Add the product handle to the link
        link += 'products/' + productHandle;
        // Add the variant ID details to redirect to a specific
        // variant ID if we are showing variants and not products
        if (distinct !== 'true') {
            link += '?variant=' + variant_id;
        }
        if ((_c = algolia === null || algolia === void 0 ? void 0 : algolia.config) === null || _c === void 0 ? void 0 : _c.analytics_enabled) {
            //Retrieving userToken
            let userToken;
            insightsClient('getUserToken', null, (err, newUserToken) => {
                if (err) {
                    console.error(err);
                    return;
                }
                userToken = newUserToken;
            });
            var clickData = {
                userToken,
                index: hit.dataset.algoliaIndex,
                eventName: 'click',
                queryID: hit.dataset.algoliaQueryid,
                objectIDs: [hit.dataset.algoliaObjectid],
                positions: [Number(hit.dataset.algoliaPosition)],
            };
            insightsClient('clickedObjectIDsAfterSearch', clickData);
            algolia === null || algolia === void 0 ? void 0 : algolia.saveForConversionTracking(clickData);
        }
        window.location.href = link;
    }, [algolia]);
    return (_jsx("swiper-slide", { children: _jsx("div", { ref: element, "data-object-id": item.objectID, onClick: handleClick }, item.objectID) }));
}
function AlgoliaRecomendation(data) {
    var _a, _b;
    const { heading } = data;
    const dragging = useRef(false);
    const recommendationRowElement = useRef(null);
    const { algolia } = useMemo(() => algoliaConfig(), []);
    const items = useMemo(() => {
        const transformedItems = DistinctFrom(data.items.map((item) => {
            var _a, _b, _c, _d, _e, _f, _g;
            const meta = item.meta;
            let price = item.price;
            let compare_at_price = item.compare_at_price;
            let sku = item.sku;
            let objectID = item.objectID;
            let _is_combined = false;
            let _distinct = (_b = (_a = algolia === null || algolia === void 0 ? void 0 : algolia.instantsearch) === null || _a === void 0 ? void 0 : _a.distinct) !== null && _b !== void 0 ? _b : false;
            const can_order = item.inventory_management !== 'shopify' || item.inventory_policy === 'continue' || item.inventory_quantity > 0;
            if (meta && meta['SB-COMBINED'] && meta['SB-COMBINED']['PRODUCT_VARIANT_ROOT']) {
                price = parseFloat((_d = (_c = meta['SB-COMBINED']['PRODUCT_VARIANT_ROOT_PRICE']) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : '0');
                compare_at_price = parseFloat((_f = (_e = meta['SB-COMBINED']['PRODUCT_VARIANT_ROOT_COMPARE_AT_PRICE']) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : '0');
                sku = meta['SB-COMBINED']['PRODUCT_VARIANT_ROOT_SKU'];
                objectID = meta['SB-COMBINED']['PRODUCT_VARIANT_ROOT'].split('/').pop();
                _is_combined = true;
                _distinct = true;
                if (meta['SB-COMBINED']['ROOT_META_FIELDS']) {
                    meta['SB-P'] = meta['SB-COMBINED']['ROOT_META_FIELDS']['SB-P'];
                    meta['SB-S'] = meta['SB-COMBINED']['ROOT_META_FIELDS']['SB-S'];
                }
            }
            return Object.assign(Object.assign({}, item), { meta, index: (_g = algolia === null || algolia === void 0 ? void 0 : algolia.instantsearch) === null || _g === void 0 ? void 0 : _g.mainIndex.getIndexName(), price,
                compare_at_price,
                sku,
                objectID,
                _is_combined,
                _distinct,
                can_order });
        }), (item) => item.objectID);
        return transformedItems;
    }, [(_a = algolia === null || algolia === void 0 ? void 0 : algolia.instantsearch) === null || _a === void 0 ? void 0 : _a.distinct, (_b = algolia === null || algolia === void 0 ? void 0 : algolia.instantsearch) === null || _b === void 0 ? void 0 : _b.mainIndex, data.items]);
    const renderItems = useMemo(() => items.length
        ? items.map((item, index) => (_jsx(AlgoliaRecomendationItem, { item: Object.assign(Object.assign({}, item), { productPosition: index + 1 }), isDragging: dragging }, item.objectID)))
        : [1, 2, 3, 4, 5, 6, 7, 8].map((key) => (_jsx("swiper-slide", { children: _jsx(EmptyCard, {}) }, `${key}`))), [items]);
    const styleButtonText = data.styleButtonText && data.styleButtonLink && (_jsx("a", Object.assign({ href: data.styleButtonLink, className: 'button-primary carousel--button', title: data.styleButtonText }, { children: data.styleButtonText })));
    useEffect(() => {
        if (recommendationRowElement.current) {
            const swiper_container = $(recommendationRowElement.current).find('swiper-container')[0];
            Object.assign(swiper_container, {
                injectStyles: SwiperInjectStyles,
            });
            swiper_container === null || swiper_container === void 0 ? void 0 : swiper_container.initialize();
        }
    }, [items]);
    return (_jsxs(_Fragment, { children: [heading && _jsx("h2", Object.assign({ className: 'product-section--title product-recommendations--title' }, { children: heading })), _jsx("div", Object.assign({ className: 'page__container' }, { children: _jsxs("div", Object.assign({ className: 'carousel--wrapper' }, { children: [_jsxs("div", Object.assign({ className: 'carousel--text-content' }, { children: [data.styleHeader && _jsx("h4", Object.assign({ className: 'carousel--text-content-title' }, { children: data.styleHeader })), data.styleContents && (_jsx("div", { className: 'carousel--text-content-contents', dangerouslySetInnerHTML: {
                                        __html: data.styleContents,
                                    } })), styleButtonText] })), _jsx("div", Object.assign({ className: clsx({
                                'ais-page': items.length > 0,
                                'skeleton-wrapper': items.length === 0,
                            }) }, { children: _jsx("div", Object.assign({ className: clsx({
                                    'ais-hits-container ais-results-as-block': items.length > 0,
                                }) }, { children: _jsx("div", Object.assign({ className: 'algolia-recommendation--row', ref: recommendationRowElement }, { children: _jsx("swiper-container", Object.assign({ "slides-per-view": 'auto', navigation: data.prevNextButtons ? 'true' : undefined, loop: 'true', autoplay: data.autoPlay ? 'true' : undefined, init: 'false' }, { children: renderItems })) })) })) })), styleButtonText] })) }))] }));
}
function AlgoliaRecomendationComponent({ renderedElement, data, rootRender }) {
    const [stateItems, setItems] = useState([]);
    useEffect(() => {
        const initialize = (data, element) => __awaiter(this, void 0, void 0, function* () {
            yield LoadSwiperJS();
            const objectIDs = commaSplit(data.ids);
            const metafieldObjectIDs = commaSplit(data.metafieldIds);
            const collectionObjectIDs = commaSplit(data.collectionIds);
            data = Object.assign(Object.assign({}, data), { objectIDs,
                metafieldObjectIDs,
                collectionObjectIDs, id: jQuery(element).attr('id') });
            const fetchStaticItems = (all_objectIds) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const { algolia } = algoliaConfig();
                if (!(data.metafieldObjectIDs.length > 0 || ((_a = data.collectionObjectIDs) === null || _a === void 0 ? void 0 : _a.length))) {
                    return [];
                }
                const filtercontentents = [];
                if (data.metafieldObjectIDs.length > 0 && (data.includeExtraSettings || !((_b = data.collectionObjectIDs) === null || _b === void 0 ? void 0 : _b.length))) {
                    all_objectIds.push(...data.metafieldObjectIDs);
                    filtercontentents.push(generateFilters(data.metafieldObjectIDs));
                }
                if (data.collectionObjectIDs.length > 0) {
                    all_objectIds.push(...data.collectionObjectIDs);
                    filtercontentents.push(generateFilters(data.collectionObjectIDs, 'collection_ids'));
                }
                try {
                    const searchClient = yield algoliaSearchClient();
                    const queries = yield searchClient.multipleQueries(filtercontentents
                        .filter((value, index, lists) => lists.indexOf(value) === index)
                        .map((filters) => ({
                        indexName,
                        query: '',
                        params: {
                            filters,
                            hitsPerPage: data.recommendationLimit,
                            analytics: true,
                            clickAnalytics: algolia === null || algolia === void 0 ? void 0 : algolia.config.analytics_enabled,
                            distinct: true,
                        },
                    })));
                    const hits = [];
                    for (const result of queries.results) {
                        const list = result.hits
                            .filter((f) => hits.findIndex((h) => h.objectID == f.objectID) === -1)
                            .map((hit) => (Object.assign(Object.assign({}, hit), { queryID: result.queryID })));
                        if (list.length > 0) {
                            hits.push(...list);
                        }
                    }
                    return hits;
                }
                catch (error) {
                    return [];
                }
            });
            const fetchRecommendations = (all_objectIds) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d;
                const { algolia } = algoliaConfig();
                if (data.excludeSimilarProducts) {
                    return [];
                }
                const currentObjectIDs = data.objectIDs.length ? data.objectIDs : data.metafieldObjectIDs;
                all_objectIds.push(...currentObjectIDs);
                if (currentObjectIDs.length === 0) {
                    return [];
                }
                const queries = currentObjectIDs
                    .filter((value, index, lists) => lists.indexOf(value) === index)
                    .map((objectID) => {
                    var _a;
                    return ({
                        objectID,
                        model: 'related-products',
                        indexName,
                        maxRecommendations: data.recommendationLimit,
                        queryParameters: {
                            filters: excludeHiddenTags,
                            analytics: true,
                            clickAnalytics: (_a = algolia === null || algolia === void 0 ? void 0 : algolia.config) === null || _a === void 0 ? void 0 : _a.analytics_enabled,
                            distinct: true,
                        },
                    });
                });
                try {
                    const recommendClient = yield algoliaRecommendClient();
                    const result = yield recommendClient.getRecommendations(queries);
                    if ((_d = (_c = result === null || result === void 0 ? void 0 : result.results) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0 > 0) {
                        const all_hits = result.results.flatMap((recommendations) => {
                            const hits = recommendations.hits.map((hit) => (Object.assign(Object.assign({}, hit), { queryID: recommendations.queryID, _distinct: true })));
                            return hits;
                        });
                        return all_hits;
                    }
                    else {
                        return [];
                    }
                }
                catch (error) {
                    return [];
                }
            });
            const fetchItems = () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const all_objectIds = [];
                    const staticItems = yield fetchStaticItems(all_objectIds);
                    const recommendations = yield fetchRecommendations(all_objectIds);
                    let items = [
                        ...staticItems,
                        ...recommendations.filter((f) => staticItems.filter((pf) => pf.objectID == f.objectID).length == 0),
                    ];
                    if (data.excludeRecommendations) {
                        items = items.filter((i) => !all_objectIds.includes(i.objectID));
                    }
                    const final_items = [];
                    const filtered_items = items
                        .filter((i) => all_objectIds.includes(i.objectID))
                        .sort((a, b) => all_objectIds.indexOf(a.objectID.toString()) - all_objectIds.indexOf(b.objectID.toString()));
                    let extra_items = items.filter((i) => !all_objectIds.includes(i.objectID));
                    if (extra_items.length > data.recommendationLimit) {
                        extra_items = extra_items.slice(0, data.recommendationLimit);
                    }
                    final_items.push(...filtered_items);
                    final_items.push(...extra_items);
                    const distinct_items = Object.values(final_items.reduce((dresult, currentItem) => {
                        if (!dresult[currentItem.objectID]) {
                            dresult[currentItem.objectID] = currentItem;
                        }
                        return dresult;
                    }, {}));
                    return distinct_items;
                }
                catch (error) { }
                return [];
            });
            const items = yield fetchItems();
            setItems(items);
        });
        initialize(data, renderedElement);
    }, [renderedElement, data]);
    useEffect(() => {
        if (stateItems.length && data) {
            rootRender(_jsx(AlgoliaRecomendation, Object.assign({}, data, { items: stateItems })));
        }
    }, [stateItems, data, renderedElement, rootRender]);
    return _jsx(_Fragment, {});
}
function InterSectionComponent({ renderedElement, data, rootRender }) {
    const [ref, show] = useIntersectionObserver(true, {
        threshold: 0.1,
        rootMargin: '900px',
    });
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: ref }), show && _jsx(AlgoliaRecomendationComponent, { data: data, renderedElement: renderedElement, rootRender: rootRender })] }));
}
export default InterSectionComponent;
